import { bookingIsVegaHeld } from '@lemontaigu/pms-core';
import type {
  BedConfig,
  BedLayout,
  BookingSearchHit,
  BookingSearchResult,
  PmsPropertyMode,
  PmsReservationStatus,
  Property,
  PropertyCode,
  ReservationView,
  Room,
  RoomStayLookup,
  RoomType,
} from '@lemontaigu/pms-core';

// PMS P1 client logic (pure — tested in logic.test.ts). The snake_case DB rows
// and their mappers moved to @lemontaigu/pms-adapters (SoC: row mapping is an
// infra concern). What stays here is the app-only presentation logic: date/euro
// formatting and the list-filter view, operating on pms-core domain types.

// ── Date helpers (ISO strings, timezone-free math) ───────────────────────────

export function isoAddDays(iso: string, days: number): string {
  const t = Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
  return new Date(t + days * 86_400_000).toISOString().slice(0, 10);
}

/** The N consecutive ISO dates starting at startIso. */
export function isoRange(startIso: string, days: number): string[] {
  return Array.from({ length: days }, (_, i) => isoAddDays(startIso, i));
}

const DAY_FMT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  timeZone: 'UTC',
});
export function fmtDayShort(iso: string): string {
  return DAY_FMT.format(new Date(`${iso}T00:00:00Z`));
}

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
});
export function fmtDate(iso: string): string {
  return DATE_FMT.format(new Date(`${iso}T00:00:00Z`));
}

/**
 * The planning's search across the établissements in reach: each building
 * answers for itself (its own services), so the answers are joined here.
 * To come and in house first, soonest first; past ones after, most recent
 * first — the order each building already used. An answer that was cut, or
 * any building's answer that says `more`, makes the whole one `more`.
 */
export function mergeBookingSearches(
  parts: readonly BookingSearchResult[],
  todayIso: string,
  limit: number,
): BookingSearchResult {
  const upcoming = (h: BookingSearchHit) => h.checkoutDate >= todayIso;
  const all = parts.flatMap((p) => p.hits);
  all.sort((a, b) => {
    const ua = upcoming(a);
    const ub = upcoming(b);
    if (ua !== ub) return ua ? -1 : 1;
    if (a.checkinDate !== b.checkinDate) {
      return ua ? a.checkinDate.localeCompare(b.checkinDate) : b.checkinDate.localeCompare(a.checkinDate);
    }
    return a.reservationId.localeCompare(b.reservationId);
  });
  return { hits: all.slice(0, limit), more: parts.some((p) => p.more) || all.length > limit };
}

/** How long ago a date was, the way the desk says it: « il y a 4 ans »,
 *  « il y a 7 mois », « ce mois-ci », or « à venir » for a booking still to
 *  come. Whole calendar months, so the 29th to the 1st is not « a month ». */
export function agoLabelFr(iso: string, today: string): string {
  if (iso > today) return 'à venir';
  const [y1, m1, d1] = iso.split('-').map(Number) as [number, number, number];
  const [y2, m2, d2] = today.split('-').map(Number) as [number, number, number];
  const months = (y2 - y1) * 12 + (m2 - m1) - (d2 < d1 ? 1 : 0);
  if (months < 1) return 'ce mois-ci';
  if (months < 12) return `il y a ${months} mois`;
  const years = Math.floor(months / 12);
  return `il y a ${years} an${years > 1 ? 's' : ''}`;
}

export function fmtEuros(cents: number): string {
  return fmtAmount(cents) + ' €';
}

/** An amount without the currency sign — the Encaissement screens, where every
 *  figure is in euros (operator 2026-09-26: « remove the € sign »). */
export function fmtAmount(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** '95' / '95,50' / '95.50' → cents; null when unparseable. */
export function parseEurosToCents(input: string): number | null {
  const cleaned = input.replace(/\s/g, '').replace(',', '.');
  if (cleaned === '' || !/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

// ── Reservation list filtering ───────────────────────────────────────────────

export type PmsFilter = 'a_venir' | 'en_cours' | 'passees' | 'annulees' | 'vad' | 'virement' | 'a_traiter' | 'toutes';

export const PMS_FILTER_LABELS: Record<PmsFilter, string> = {
  a_venir: 'À venir',
  // « En séjour » is right HERE even though it is deliberately not the label of
  // the `checked_in` STATUS: this is a filter over « who is currently in the
  // hotel », so when `in_house` becomes its own state it should match BOTH —
  // and the filter predicate below is what will need widening, not this word.
  en_cours: 'En séjour',
  passees: 'Passées',
  annulees: 'Annulées',
  // A5 (RESERVATION-API-BRIEF §8.6): web bookings whose deposit the desk has
  // not yet keyed into the bank VAD. Not limited to the future — a late
  // guest's unpaid deposit is still a task.
  vad: 'VAD à traiter',
  // PB10: arrhes by bank transfer, confirmed and waiting for the money.
  virement: 'Virements à recevoir',
  // OB4: the PMS's own bookings whose dates have passed, still unresolved.
  a_traiter: 'À traiter',
  toutes: 'Toutes',
};

/** OB1: each établissement's `app_property.mode`, by code, as the reach read it. */
export type PropertyModes = ReadonlyMap<string, PmsPropertyMode | undefined>;

export function propertyModes(properties: readonly Pick<Property, 'code' | 'mode'>[]): PropertyModes {
  return new Map(properties.map((p) => [p.code, p.mode]));
}

/** OB1: is this row « tenue par Vega » — read-only here, its state changed in
 *  Vega (PMS-OWNED-BOOKINGS-BRIEF §3). An établissement missing from `modes`
 *  counts as not live, like every other unknown on this question. */
export function rowIsVegaHeld(
  modes: PropertyModes,
  r: { propertyCode: string; sourceVegaResa?: string | null },
): boolean {
  return bookingIsVegaHeld(r, modes.get(r.propertyCode));
}

// ── OB4 — « À traiter » (PMS-OWNED-BOOKINGS-BRIEF §4, Q4) ─────────────────────
//
// A logement of a booking the PMS owns whose dates have passed and that nobody
// settled: still *Réservée* / *Confirmée* after its arrival day (check-in late,
// no-show or cancel), or still *Arrivée effectuée* after its departure day
// (check-out, or « Annuler l'arrivée »). « Today » is the calendar date in
// Europe/Paris, not the PMS current day (Q4): in proof the current day follows
// Vega, frozen since LMTG left the LAN.

export type OverdueKind = 'arrival_passed' | 'departure_passed';

export const OVERDUE_LABELS_FR: Record<OverdueKind, string> = {
  arrival_passed: 'Arrivée passée',
  departure_passed: 'Départ passé',
};

interface DatedState {
  status: PmsReservationStatus;
  checkinDate: string;
  checkoutDate: string;
  cancelledAt?: string | null;
}

export function stayOverdue(s: DatedState, todayIso: string): OverdueKind | null {
  if (s.cancelledAt != null || s.status === 'cancelled') return null;
  if ((s.status === 'reserved' || s.status === 'confirmed') && s.checkinDate < todayIso) return 'arrival_passed';
  if (s.status === 'checked_in' && s.checkoutDate < todayIso) return 'departure_passed';
  return null;
}

/** The booking's first overdue logement, or null — PMS-owned bookings only: a
 *  Vega-held one is settled in Vega. Without its logements, the booking's own
 *  (derived) dates and status stand in. */
export function bookingOverdue(
  r: ReservationView,
  stays: readonly DatedState[] | undefined,
  todayIso: string,
  modes: PropertyModes,
): OverdueKind | null {
  if (rowIsVegaHeld(modes, r)) return null;
  const list = stays !== undefined && stays.length > 0 ? stays : [r];
  for (const s of list) {
    const k = stayOverdue(s, todayIso);
    if (k !== null) return k;
  }
  return null;
}

/** `pmsOnly`: the « Réservations PMS » chip — when given, only the bookings
 *  the PMS owns are kept, on top of the status filter. */
export function filterReservations(
  rows: ReservationView[],
  filter: PmsFilter,
  todayIso: string,
  search: string,
  pmsOnly: PropertyModes | null = null,
  /** OB4: what « À traiter » reads — each établissement's mode and each booking's logements. */
  overdue: { modes: PropertyModes; staysOf: ReadonlyMap<string, readonly DatedState[]> } | null = null,
): ReservationView[] {
  const q = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (pmsOnly !== null && rowIsVegaHeld(pmsOnly, r)) return false;
    if (
      filter === 'a_traiter' &&
      bookingOverdue(r, overdue?.staysOf.get(r.id), todayIso, overdue?.modes ?? new Map()) === null
    )
      return false;
    if (filter === 'a_venir' && !((r.status === 'reserved' || r.status === 'confirmed') && r.checkoutDate >= todayIso))
      return false;
    if (filter === 'en_cours' && r.status !== 'checked_in') return false;
    if (filter === 'passees' && !(r.status === 'checked_out' || (r.status !== 'cancelled' && r.checkoutDate < todayIso)))
      return false;
    if (filter === 'annulees' && r.status !== 'cancelled') return false;
    if (filter === 'vad' && !(r.guarantee === 'vad_pending' && r.status !== 'cancelled')) return false;
    if (filter === 'virement' && !(r.guarantee === 'transfer_pending' && r.status !== 'cancelled')) return false;
    if (q) {
      const hay = `${r.guest?.lastName ?? ''} ${r.guest?.firstName ?? ''} ${r.channelRef ?? ''} ${r.channel ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

// ── Room configuration table (« Configuration hôtel ») ───────────────────────
//
// The operator's Configuration Chambre.xlsx rendered as a live table over
// pms_room + pms_room_type: same columns, same order, plus the category LABEL
// next to its code. Pure view-model + sort/filter so the component stays a
// renderer (docs/ARCHITECTURE.md — functional core, no logic in the driver).

// ── Bed layout — the room's TYPE read as actual beds ────────────────────────
//
// The mapping itself is no longer here. It is reference DATA in `pms_bed_config`
// (room-edit brief E1), fetched through ReferenceService.bedConfigs() and turned
// into a lookup below. Adding a bed type is now data entry, not a deploy — which
// is the whole point: "King" cost a code change AND a migration on 2026-08-06.
//
// What stays in this app is presentation: the French phrasing, the sort rank,
// and the null handling. `BedLayout` itself lives in pms-core, where domain
// knowledge belongs.

/** Code → layout, built once per load from the reference data. */
export type BedLayoutLookup = ReadonlyMap<string, BedLayout>;

export function bedLayoutLookup(configs: BedConfig[]): BedLayoutLookup {
  return new Map(configs.map((c) => [c.code, c.layout]));
}

/** null = a TYPE the vocabulary has no reading for — « — », never guessed. */
export function bedLayoutFor(lookup: BedLayoutLookup, bedConfig: string | null): BedLayout | null {
  if (bedConfig === null) return null;
  return lookup.get(bedConfig.trim()) ?? null;
}

const beds = (n: number, one: string, many: string, size?: string) =>
  n > 0 ? `${n} lit${n > 1 ? 's' : ''} ${n > 1 ? many : one}${size ? ` (${size})` : ''}` : '';

/** ⚠ The double's SIZE is the ROOM's fact, not the arrangement's (0173).
 *  `Double` is satisfied by a Queen or a King, so the phrase cannot be built
 *  from the layout alone — passing the room's `doubleBedType` is what replaced
 *  the old `King` pseudo-configuration. */
const bedsPhrase = (doubles: number, singles: number, doubleBedType: string | null): string =>
  [
    doubleBedType === 'King'
      ? beds(doubles, 'king', 'king', '180 cm')
      : beds(doubles, 'double', 'doubles', '160 cm'),
    beds(singles, 'simple', 'simples'),
  ]
    .filter(Boolean)
    .join(' + ');

/** The tooltip: what the pictograms mean, in words.
 *
 *  Convertibility is NOT here any more — it is a property of the room (how many
 *  arrangements it has), not of any one arrangement. `bedConfigsLabel` below
 *  says it for a whole room. */
export function bedLayoutLabel(layout: BedLayout | null, doubleBedType: string | null = null): string {
  if (layout === null) return 'Configuration des lits inconnue';
  const parts = [bedsPhrase(layout.doubles, layout.singles, doubleBedType)];
  if (layout.inferred) parts.push('déduit du nombre de personnes — à confirmer');
  return parts.join(' · ');
}

/** The tooltip for a whole room: every arrangement it can take, and which one
 *  it is standing in when that is known. « Not known » is stated, never guessed
 *  — there is no default to fall back to (0173). */
export function bedConfigsLabel(
  layouts: readonly { code: string; layout: BedLayout | null }[],
  doubleBedType: string | null,
  current: string | null,
): string {
  if (layouts.length === 0) return 'Configuration des lits inconnue';
  const parts = layouts.map(
    (l) => `${l.code} : ${bedLayoutLabel(l.layout, doubleBedType)}`,
  );
  if (layouts.length > 1) {
    parts.push(
      current === null
        ? 'configuration actuelle non connue'
        : `actuellement : ${current}`,
    );
  }
  return parts.join(' · ');
}

/** Sort scalar for the pictogram column: capacity first, then bed size.
 *  A King outranks a Queen at equal capacity, which is why it takes the room's
 *  bed type rather than reading a `kings` count that no longer exists. */
export function bedLayoutRank(layout: BedLayout | null, doubleBedType: string | null = null): number | null {
  if (layout === null) return null;
  const sleeps = layout.doubles * 2 + layout.singles;
  const kingBonus = doubleBedType === 'King' ? layout.doubles : 0;
  return sleeps * 1000 + kingBonus * 100 + layout.doubles * 10 + layout.singles;
}

// ── Outlook — what the room looks onto (« SUP → balcon, sinon la vue ») ──────
//
// One glyph carrying the two facts a desk agent actually asks for. A supérieure
// has a balcony, and that outranks the view (operator, 2026-08-06); every other
// room shows what it faces. The view is still in the tooltip either way, so
// nothing the sheet holds is lost by collapsing the two columns into one.

export type OutlookKind = 'balcony' | 'castle' | 'garden' | 'city' | 'unknown';

export function outlookKind(isSuperior: boolean, view: string | null): OutlookKind {
  if (isSuperior) return 'balcony';
  switch (view) {
    case 'Château':
      return 'castle';
    case 'Jardin':
      return 'garden';
    case 'Ville':
      return 'city';
    default:
      return 'unknown';
  }
}

export function outlookLabel(isSuperior: boolean, view: string | null): string {
  const vue = view === null ? 'vue non renseignée' : `vue ${view}`;
  return isSuperior ? `Supérieure — balcon · ${vue}` : vue.charAt(0).toUpperCase() + vue.slice(1);
}

/** Sort scalar: balconies first, then by view — a glyph can't be compared. */
export function outlookKey(isSuperior: boolean, view: string | null): string {
  return `${isSuperior ? '0' : '1'}-${view ?? 'zzz'}`;
}

/** One xlsx row: the physical room joined to its category. */
export interface RoomConfigRow {
  id: number;
  /** Its établissement (U3) — the table lists every one in reach. */
  propertyCode: string;
  roomNumber: string;
  floor: number;
  maxOccupancy: number | null; // xlsx « Nbre »
  categoryCode: string | null; // xlsx « Categorie » (DSTD…), null = untyped
  categoryLabel: string | null; // the category's French label
  /** Every arrangement the room can take (0173). One = not convertible. */
  configOptions: readonly string[];
  /** Which one it is STANDING in, when anyone has said. No default exists. */
  currentBedConfig: string | null;
  currentBedConfigSource: Room['currentBedConfigSource'];
  /** Queen | King | null — the room's furniture, not its arrangement. */
  doubleBedType: string | null;
  /** Each arrangement read as beds — the pictogram column. */
  bedLayouts: readonly { code: string; layout: BedLayout | null }[];
  bedsRank: number | null; // its sort scalar (a pictogram can't be compared)
  outlook: OutlookKind; // balcon (SUP) sinon la vue — the glyph
  outlookKey: string; // its sort scalar
  isSuperior: boolean; // xlsx « SUP »
  heatingZone: number | null; // xlsx « Zone CH »
  bathroomHeatingZone: number | null; // xlsx « Zone SDB »
  view: string | null; // xlsx « VUE »
  hasShower: boolean; // xlsx « Douche ? »
  wcNotSeparated: boolean; // xlsx « WC non séparé »
  remarks: string | null; // xlsx « REM »
}

export function roomConfigRows(
  rooms: Room[],
  types: RoomType[],
  bedConfigs: BedConfig[],
): RoomConfigRow[] {
  const byId = new Map(types.map((t) => [t.id, t]));
  const lookup = bedLayoutLookup(bedConfigs);
  return rooms.map((r) => {
    const t = r.roomTypeId === null ? undefined : byId.get(r.roomTypeId);
    const bedLayouts = r.configOptions.map((code) => ({
      code,
      layout: bedLayoutFor(lookup, code),
    }));
    // The row sorts on the arrangement it is STANDING in when that is known,
    // and otherwise on its smallest — never on « the first », which would sort
    // by alphabet and look like a meaning.
    const ranked =
      bedLayouts.find((l) => l.code === r.currentBedConfig)?.layout ??
      bedLayouts
        .map((l) => l.layout)
        .filter((l): l is BedLayout => l !== null)
        .sort((a, b) => (bedLayoutRank(a, r.doubleBedType) ?? 0) - (bedLayoutRank(b, r.doubleBedType) ?? 0))[0] ??
      null;
    return {
      id: r.id,
      propertyCode: r.propertyCode,
      roomNumber: r.roomNumber,
      floor: r.floor,
      maxOccupancy: r.maxOccupancy,
      categoryCode: t?.code ?? null,
      categoryLabel: t?.labelFr ?? null,
      configOptions: r.configOptions,
      currentBedConfig: r.currentBedConfig,
      currentBedConfigSource: r.currentBedConfigSource,
      doubleBedType: r.doubleBedType,
      bedLayouts,
      bedsRank: bedLayoutRank(ranked, r.doubleBedType),
      outlook: outlookKind(r.isSuperior, r.view),
      outlookKey: outlookKey(r.isSuperior, r.view),
      isSuperior: r.isSuperior,
      heatingZone: r.heatingZone,
      bathroomHeatingZone: r.bathroomHeatingZone,
      view: r.view,
      hasShower: r.hasShower,
      wcNotSeparated: r.wcNotSeparated,
      remarks: r.remarks,
    };
  });
}

// `beds`/`outlook` are shapes, not scalars — those columns sort on their *Key.
export type RoomConfigSortKey = keyof Omit<
  RoomConfigRow,
  | 'id'
  | 'categoryLabel'
  | 'bedLayouts'
  | 'configOptions'
  | 'currentBedConfigSource'
  | 'outlook'
>;

export interface RoomConfigFilters {
  /** '' = tous. A FILTER on what is shown — never a scope for writing (M11′ 3). */
  etablissement: string;
  bedConfig: string; // '' = toutes
  category: string; // '' = toutes (matches categoryCode)
  heatingZone: string; // '' = toutes
  search: string;
}

export const EMPTY_ROOM_CONFIG_FILTERS: RoomConfigFilters = {
  etablissement: '',
  bedConfig: '',
  category: '',
  heatingZone: '',
  search: '',
};

// ── Category order — one canonical sequence for the whole PMS ────────────────
//
// The operator's reading order (2026-08-07): SUPÉRIEURE BEFORE STANDARD within
// each size, sizes ascending. Alphabetical put DSTD before DSUP and FAM before
// FAMS — the opposite of how he reads a category list, and different again in
// every dropdown. Every list of categories sorts through `byRoomCategory`:
// the Configuration filter, the Calendrier assignment select, the Catégorie
// column. A code missing from this list sorts last (alphabetically among its
// peers) rather than throwing — a new category must not break a dropdown.
export const ROOM_CATEGORY_ORDER = ['DSUP', 'DSTD', 'TSUP', 'TSTD', 'FAMS', 'FAM'] as const;

export function roomCategoryRank(code: string | null): number {
  const i = code === null ? -1 : (ROOM_CATEGORY_ORDER as readonly string[]).indexOf(code);
  return i === -1 ? ROOM_CATEGORY_ORDER.length : i;
}

/** Comparator over category codes — the canonical order, unknowns last. */
export function byRoomCategory(a: string | null, b: string | null): number {
  const r = roomCategoryRank(a) - roomCategoryRank(b);
  return r !== 0 ? r : (a ?? '').localeCompare(b ?? '', 'fr');
}

/** Sorts anything carrying a category `code` (e.g. RoomType) into that order. */
export function sortByRoomCategory<T extends { code: string }>(items: T[]): T[] {
  return [...items].sort((x, y) => byRoomCategory(x.code, y.code));
}

/** Distinct filter values, sorted — derived from the rows, never hardcoded. */
export function roomConfigOptions(rows: RoomConfigRow[]): {
  bedConfigs: string[];
  categories: string[];
  heatingZones: string[];
} {
  const distinct = (values: (string | null)[], cmp = (a: string, b: string) => a.localeCompare(b, 'fr')) =>
    [...new Set(values.filter((v): v is string => v !== null && v !== ''))].sort(cmp);
  return {
    // Every arrangement any room can take — a convertible room contributes
    // both of its own, because the filter asks « can be made up as », not
    // « is nominally ».
    bedConfigs: distinct(rows.flatMap((r) => [...r.configOptions])),
    categories: distinct(rows.map((r) => r.categoryCode), byRoomCategory),
    heatingZones: distinct(rows.map((r) => (r.heatingZone === null ? null : String(r.heatingZone)))),
  };
}

export function filterRoomConfig(rows: RoomConfigRow[], f: RoomConfigFilters): RoomConfigRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    // « Can this room be made up as X » — so room 108 answers both Double and
    // Twin. Under the old scalar it answered only to the literal « Double -
    // Twin », which is why the filter listed codes nobody thinks in.
    if (f.etablissement && r.propertyCode !== f.etablissement) return false;
    if (f.bedConfig && !r.configOptions.includes(f.bedConfig)) return false;
    if (f.category && r.categoryCode !== f.category) return false;
    if (f.heatingZone && String(r.heatingZone ?? '') !== f.heatingZone) return false;
    if (q) {
      const hay = `${r.roomNumber} ${r.categoryCode ?? ''} ${r.categoryLabel ?? ''} ${r.configOptions.join(' ')} ${r.doubleBedType ?? ''} ${r.view ?? ''} ${r.remarks ?? ''}`;
      if (!hay.toLowerCase().includes(q)) return false;
    }
    return true;
  });
}

// ── Preset orders — the three ways the operator reads this table ─────────────
//
// A column sort answers "what is biggest"; these answer "how do I work the
// list". Zone in particular is not a numeric sort: the zones rank 2 · 1 · 3,
// the order the building is shut down in (the sheet's own footnotes — zone 3
// closes at low occupancy, zone 1 when the restaurant is closed), so a plain
// ascending sort on the column would put them in a useless order.

export type RoomConfigPreset = 'defaut' | 'zone' | 'numero';

export const ROOM_CONFIG_PRESETS: { id: RoomConfigPreset; label: string; title: string }[] = [
  { id: 'defaut', label: 'Par défaut', title: 'Nbre de personnes, puis SUP avant standard, puis n° de chambre' },
  { id: 'zone', label: 'Zone', title: 'Zone CH (2, 1, 3), puis zone SdB (sans zone d’abord), puis nbre, SUP, n°' },
  { id: 'numero', label: 'N° de chambre', title: 'N° de chambre croissant' },
];

type Cmp = (a: RoomConfigRow, b: RoomConfigRow) => number;

const chain =
  (...cmps: Cmp[]): Cmp =>
  (a, b) => {
    for (const cmp of cmps) {
      const r = cmp(a, b);
      if (r !== 0) return r;
    }
    return 0;
  };

/** Heating zones rank 2 · 1 · 3 — the shutdown order, not the number line. */
const ZONE_CH_ORDER = [2, 1, 3];
const zoneChRank = (z: number | null): number => {
  const i = z === null ? -1 : ZONE_CH_ORDER.indexOf(z);
  return i === -1 ? ZONE_CH_ORDER.length : i; // unknown/none last
};

const byNbre: Cmp = (a, b) => (a.maxOccupancy ?? Infinity) - (b.maxOccupancy ?? Infinity);
/** SUP before standard. */
const bySup: Cmp = (a, b) => Number(b.isSuperior) - Number(a.isSuperior);
const byNo: Cmp = (a, b) => a.roomNumber.localeCompare(b.roomNumber, 'fr');
const byZoneCh: Cmp = (a, b) => zoneChRank(a.heatingZone) - zoneChRank(b.heatingZone);
/** Rooms with no separate bathroom zone first, then by zone. */
const byZoneSdb: Cmp = (a, b) => (a.bathroomHeatingZone ?? -1) - (b.bathroomHeatingZone ?? -1);

const PRESET_ORDER: Record<RoomConfigPreset, Cmp> = {
  defaut: chain(byNbre, bySup, byNo),
  zone: chain(byZoneCh, byZoneSdb, byNbre, bySup, byNo),
  numero: byNo,
};

export function sortRoomConfigPreset(rows: RoomConfigRow[], preset: RoomConfigPreset): RoomConfigRow[] {
  return [...rows].sort(PRESET_ORDER[preset]);
}

/** Stable sort; nulls always last whatever the direction (they carry no order). */
export function sortRoomConfig(
  rows: RoomConfigRow[],
  key: RoomConfigSortKey,
  dir: 'asc' | 'desc',
): RoomConfigRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    const aNull = av === null || av === '';
    const bNull = bv === null || bv === '';
    if (aNull || bNull) {
      if (aNull && bNull) return a.roomNumber.localeCompare(b.roomNumber, 'fr');
      return aNull ? 1 : -1;
    }
    let cmp: number;
    // Catégorie is not alphabetical — it has a canonical order (SUP before
    // standard within each size), the same one the dropdowns use.
    if (key === 'categoryCode') cmp = byRoomCategory(String(av), String(bv));
    else if (typeof av === 'boolean' && typeof bv === 'boolean') cmp = Number(av) - Number(bv);
    else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv), 'fr');
    // Room number breaks every tie, so the table never reshuffles arbitrarily.
    return cmp === 0 ? a.roomNumber.localeCompare(b.roomNumber, 'fr') : cmp * sign;
  });
}

// ── Which établissement a session lands on (P1.6 step 1) ─────────────────────

/**
 * Given the actor's properties — already in presentation order — the default
 * établissement: the one the scoped services bind to and a page about one
 * établissement opens on.
 *
 * It is the fix for the P1.6 ambiguity cliff (build brief §3): returning null
 * for a multi-property actor leaves the scope at `{kind:'actor'}`, which makes
 * `actor_default_property()` NULL in the database and every RPC raise
 * « établissement non résolu ». The Calendrier fails to load; it does not
 * degrade to an empty grid.
 *
 * So null means one thing only: **the actor holds no property at all.**
 *
 * Until U4 a choice remembered by the header switcher came first. The switcher
 * went with U4 (PMS-UNIFIED-DESK-BRIEF §8), and so did the memory of it.
 */
export function pickProperty(list: readonly Property[]): PropertyCode | null {
  if (list.length === 0) return null;
  // The first in PRESENTATION order. `listForActor` has already sorted by
  // `app_property.sort_order`, which is precisely why this is `[0]` and not a
  // `.sort()` — re-sorting here would hand the choice back to the alphabet,
  // and the alphabet prefers APPART to HOTEL.
  return list[0]!.code;
}

// ── A booking's default plan, in ITS établissement (U3) ──────────────────────

/**
 * The plan a room created with no plan takes: its établissement's first. With
 * lists spanning several établissements, « the first plan » of the whole list
 * would be another building's — refused by the writer, whose categories and
 * plan must agree (the composite foreign keys).
 */
export function defaultPlanFor<T extends { propertyCode: string }>(plans: readonly T[], code: string | null): T | null {
  return (code === null ? plans[0] : plans.find((p) => p.propertyCode === code)) ?? null;
}

// ── Creating with no anchor: which établissement (U2) ────────────────────────

export type CreateTargetDecision<T> = { kind: 'none' } | { kind: 'only'; item: T } | { kind: 'ask' };

/**
 * Whether a form that creates something with no anchor must ask « dans quel
 * établissement ? ». One établissement in reach: no question, it is that one.
 * Several: ask, with no default (UD2 — a default is the screen deciding). None:
 * nothing can be created, and the form says so rather than guessing.
 */
export function createTargetDecision<T>(reach: readonly T[]): CreateTargetDecision<T> {
  if (reach.length === 0) return { kind: 'none' };
  if (reach.length === 1) return { kind: 'only', item: reach[0]! };
  return { kind: 'ask' };
}

// ── The till's room field: one stay, none, or ask (U0) ───────────────────────

export type RoomLookupOutcome =
  | { kind: 'none' }
  | { kind: 'one'; stay: RoomStayLookup }
  | { kind: 'choose'; stays: readonly RoomStayLookup[] };

/**
 * What the room field does with the lookup's answer. The lookup returns at most
 * one stay PER ÉTABLISSEMENT in reach (`pms_lookup_room_stay`, U0), so more than
 * one means the number exists in several buildings today.
 *
 * Then the desk is asked, never answered for: a payment taken against the
 * first match would be filed in a building nobody chose — the
 * `PMS-UNIFIED-DESK-BRIEF.md` §4 rule that ambiguity is a refusal, never `[0]`.
 */
export function roomLookupOutcome(stays: readonly RoomStayLookup[]): RoomLookupOutcome {
  if (stays.length === 0) return { kind: 'none' };
  if (stays.length === 1) return { kind: 'one', stay: stays[0]! };
  return { kind: 'choose', stays };
}

// ── « Plan d'occupation » — the per-unit rack (GLOSSARY: `rack`) ─────────────
//
// The calendar answers « how many DSTD are free on Thursday »; the rack answers
// « is Apt2 free on Thursday ». Different question, different shape: one row per
// UNIT, reservations drawn as bars across the days. P1.6 step 6 needed the
// second and the calendar could not express it.
//
// All of it is pure so the arithmetic is testable without a browser — the bar
// geometry in particular, which is the part that goes subtly wrong (a departure
// day is NOT an occupied night, and a bar clipped by the window edge must still
// start in the right column).

/** Statuses that actually hold a unit. `cancelled` never occupies; a
 *  `checked_out` stay did occupy, and must keep showing in a past window —
 *  hiding it would make history look free. */
export const RACK_OCCUPYING: readonly PmsReservationStatus[] = [
  'reserved',
  'confirmed',
  'checked_in',
  'checked_out',
];

// ── What the rack READS (2026-09-30) ─────────────────────────────────────────
//
// The rack used to load every stay, and past 1 000 rows PostgREST cut that read
// with no error — the stays it dropped were the furthest-future ones, whose
// rooms then looked free. It now reads a PERIOD through `rackWindow` (see
// `RackWindow` in pms-core); these decide which period, and whether what was
// read covers what is being asked of it.

/** Days read beyond each edge of the window shown — one « » step, so paging a
 *  week either way is drawn from what is already read while the next read is
 *  in flight. */
export const RACK_READ_PAD_DAYS = 7;

/** The longest period `pms_rack_window` accepts; it refuses beyond. */
export const RACK_READ_MAX_DAYS = 400;

/** A half-open period of ISO dates: the nights from `from` to the eve of `to`. */
export interface IsoPeriod {
  from: string;
  to: string;
}

const daysBetween = (from: string, to: string) =>
  Math.round(
    (Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10))) -
      Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)))) /
      86_400_000,
  );

/**
 * The period the rack must read to draw [from, to) AND to rank proposals for
 * every stay in « À attribuer »:
 *   * the window, padded RACK_READ_PAD_DAYS each side;
 *   * widened to the WHOLE of every unassigned stay with a night in the window.
 *     A proposal tests every night of the stay, and a night nobody read looks
 *     free. Inside the period read, `rackWindow` makes the neighbour rules exact
 *     (it adds each unit's nearest stay either side), so « inside » is the
 *     whole requirement.
 *
 * A widening that would pass RACK_READ_MAX_DAYS is not made: the RPC would
 * refuse the whole rack for the sake of one stay. That stay's proposals are
 * reported unavailable instead (`rackCovers` says no), never guessed.
 *
 * Called with no stays for the first read, then with what it returned: a wider
 * answer means one more read.
 */
export function rackReadPeriod(
  from: string,
  to: string,
  stays: readonly { roomId: number | null; status: PmsReservationStatus; checkinDate: string; checkoutDate: string }[] = [],
): IsoPeriod {
  let lo = isoAddDays(from, -RACK_READ_PAD_DAYS);
  let hi = isoAddDays(to, RACK_READ_PAD_DAYS);
  for (const s of stays) {
    if (s.roomId !== null || !RACK_OCCUPYING.includes(s.status)) continue;
    if (!(s.checkinDate < to && s.checkoutDate > from)) continue;
    const wideLo = s.checkinDate < lo ? s.checkinDate : lo;
    const wideHi = s.checkoutDate > hi ? s.checkoutDate : hi;
    if (daysBetween(wideLo, wideHi) > RACK_READ_MAX_DAYS) continue;
    lo = wideLo;
    hi = wideHi;
  }
  return { from: lo, to: hi };
}

/** Whether what was read covers every night of [from, to). */
export function rackCovers(read: IsoPeriod | null, from: string, to: string): boolean {
  return read !== null && read.from <= from && read.to >= to;
}

export interface RackBar {
  startIndex: number;
  /** Number of NIGHT columns covered inside the window. */
  span: number;
  /** The stay began before the window / ends after it — drawn as a cut edge so
   *  a clipped bar is never mistaken for a short one. */
  clippedStart: boolean;
  clippedEnd: boolean;
}

/**
 * Where a stay sits in a day window, or null when it falls entirely outside.
 *
 * The night convention is the one the whole domain uses: a stay occupies
 * `checkin` … `checkout − 1`. The departure day is free for the next arrival,
 * which is why the rack can show back-to-back stays without them overlapping.
 */
export function rackBar(
  stay: { checkinDate: string; checkoutDate: string },
  days: readonly string[],
): RackBar | null {
  if (days.length === 0) return null;
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const lastNight = isoAddDays(stay.checkoutDate, -1);
  // A zero-night row cannot occupy anything (the RPC refuses one anyway).
  if (lastNight < stay.checkinDate) return null;
  if (lastNight < first || stay.checkinDate > last) return null;

  const startIso = stay.checkinDate < first ? first : stay.checkinDate;
  const endIso = lastNight > last ? last : lastNight;
  const startIndex = days.indexOf(startIso);
  const endIndex = days.indexOf(endIso);
  if (startIndex < 0 || endIndex < startIndex) return null;

  return {
    startIndex,
    span: endIndex - startIndex + 1,
    clippedStart: stay.checkinDate < first,
    clippedEnd: lastNight > last,
  };
}

export interface RackGroup<TType, TRoom> {
  /** null = the « non typées » group, which is a legitimate state: a room the
   *  operator has not categorised yet (0057 seeded all 42 that way). */
  type: TType | null;
  rooms: TRoom[];
}

/**
 * Units grouped under their category, categories in the operator's canonical
 * order, untyped units last.
 *
 * Reuses `sortByRoomCategory` rather than sorting here, so the rack, the
 * calendar and Configuration can never disagree about what order categories
 * come in — the same argument Calendrier makes about its own list.
 */
export function rackGroups<
  TType extends { id: number; code: string },
  TRoom extends { roomTypeId: number | null; roomNumber: string },
>(rooms: readonly TRoom[], roomTypes: readonly TType[]): RackGroup<TType, TRoom>[] {
  const byNumber = (a: TRoom, b: TRoom) =>
    a.roomNumber.localeCompare(b.roomNumber, 'fr', { numeric: true });

  const groups = sortByRoomCategory([...roomTypes]).map((type) => ({
    type,
    rooms: rooms.filter((r) => r.roomTypeId === type.id).sort(byNumber),
  }));

  const untyped = rooms.filter((r) => r.roomTypeId === null).sort(byNumber);
  return untyped.length > 0 ? [...groups, { type: null, rooms: untyped }] : groups;
}

/**
 * « mer 12 » — the compact day caption the rack needs.
 *
 * `fmtDayShort` gives « mer. 12/08 », which is right for a sentence and far too
 * wide for a column: at 25 days across, the captions collided and ran into each
 * other (operator screenshot, 2026-08-11). The month is not lost — it moves to
 * the band above (`rackMonthBands`), which is where Vega carries it too.
 */
const DAY_NAME_FMT = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', timeZone: 'UTC' });
export function fmtDayCompact(iso: string): string {
  const name = DAY_NAME_FMT.format(new Date(`${iso}T00:00:00Z`)).replace('.', '');
  return `${name} ${iso.slice(8, 10)}`;
}

// ── The row height — what the screen can afford ──────────────────────────────
//
// The rack's target is the whole hotel on one screen with no vertical scroll
// (see Rack.tsx, DENSITY). A fixed row height can only serve one monitor: sized
// for the 1920x1200 reception desk it leaves a third of a 4K screen blank. So
// the row height is the largest that still fits — never below the old fixed
// 1.2rem (which is what the reception desk gets, near enough), never above
// `RACK_ROW_MAX_REM`, past which a row is just a taller strip of nothing.
//
// Everything is in rem so it follows the browser's own zoom. `scale` is the
// row's growth over the floor, capped: the bar captions follow the row, but
// only so far — the day columns do not grow, and a caption that doubled would
// eat every short stay's name.

/** The pre-2026-10-18 fixed row height, now the floor. */
export const RACK_ROW_MIN_REM = 1.2;
export const RACK_ROW_MAX_REM = 1.9;
export const RACK_SCALE_MAX = 1.25;
/** The sticky header: the month band (1.1rem) and the day captions (1.4rem). */
export const RACK_HEAD_REM = 2.5;
/** The « À attribuer » heading, when there is one. */
export const RACK_GROUP_REM = 1.1;
/** The wrapper's border (2px) and a little slack, so a perfect fit never grows
 *  a one-pixel scrollbar. */
const RACK_FIT_SLACK_REM = 0.15;

export interface RackRowFit {
  /** The row height, rem. */
  rowRem: number;
  /** 1 at the floor, up to `RACK_SCALE_MAX` — what the captions multiply by. */
  scale: number;
}

/** The row height that makes `rows` rows (the unit rows, the « Saison » row and
 *  the unassigned stays — anything drawn at the row height) fill `availRem`,
 *  the height the grid wrapper has. Falls back to the floor on nothing sensible
 *  to divide by, so a test environment with no layout gets today's geometry. */
export function rackRowFit(availRem: number, rows: number, hasGroupBand: boolean): RackRowFit {
  const free = availRem - RACK_HEAD_REM - RACK_FIT_SLACK_REM - (hasGroupBand ? RACK_GROUP_REM : 0);
  const each = rows > 0 && Number.isFinite(free) ? Math.floor((free / rows) * 1000) / 1000 : RACK_ROW_MIN_REM;
  const rowRem = Math.min(RACK_ROW_MAX_REM, Math.max(RACK_ROW_MIN_REM, each));
  const scale = Math.min(RACK_SCALE_MAX, Math.max(1, rowRem / RACK_ROW_MIN_REM));
  return { rowRem, scale: Math.round(scale * 1000) / 1000 };
}

export interface RackMonthBand {
  label: string;
  /** How many day columns of the window this month covers. */
  span: number;
}

const MONTH_FMT = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** The window's days folded into month runs, so the header can say « Août 2026 »
 *  once over its columns instead of repeating the month 25 times. */
export function rackMonthBands(days: readonly string[]): RackMonthBand[] {
  const out: RackMonthBand[] = [];
  for (const iso of days) {
    const label = MONTH_FMT.format(new Date(`${iso}T00:00:00Z`));
    const last = out[out.length - 1];
    if (last && last.label === label) last.span += 1;
    else out.push({ label, span: 1 });
  }
  return out;
}

/** True for Saturday and Sunday — the rack tints weekends, which is how a human
 *  finds a date in a 25-column grid without counting. */
export function isWeekend(iso: string): boolean {
  const d = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return d === 0 || d === 6;
}

// ── What a bar says about its booking (operator, 2026-09-30) ─────────────────
//
// « can we add bed, arrivals, comments… in the reservation info » — asked for
// on the bar, in its tooltip and in the inspector alike. The three share these
// formatters, so the bar and the pane cannot disagree about a booking.

/** « 18:00:00 » → « 18h », « 18:30:00 » → « 18h30 ». Postgres `time` text. */
function fmtClock(t: string): string {
  const h = String(Number(t.slice(0, 2)));
  const m = t.slice(3, 5);
  return m === '00' || m === '' ? `${h}h` : `${h}h${m}`;
}

/** The channel's arrival window as the desk says it: « 18h–19h », « dès 18h »,
 *  « avant 19h ». Null when the channel gave none — nothing is guessed. */
export function fmtArrivalWindow(from: string | null | undefined, to: string | null | undefined): string | null {
  const f = from ?? null;
  const t = to ?? null;
  if (f !== null && t !== null) return f === t ? fmtClock(f) : `${fmtClock(f)}–${fmtClock(t)}`;
  if (f !== null) return `dès ${fmtClock(f)}`;
  if (t !== null) return `avant ${fmtClock(t)}`;
  return null;
}

/** The comment worth a glance: the STAFF note first — it is ours and it is
 *  deliberate — else the channel's own text. Whitespace folded to one line. */
export function bookingComment(r: { notes: string | null; sourceText?: string | null } | null): string | null {
  const pick = [r?.notes, r?.sourceText].find((t) => t != null && t.trim() !== '');
  return pick === undefined || pick === null ? null : pick.replace(/\s+/g, ' ').trim();
}

/** The facts a bar appends after the name, in reading order. Only what is
 *  known: a booking with no request shows its name alone, as before. The CSS
 *  cuts whatever does not fit, so a two-night bar at 25 days keeps the name. */
export function rackBarFacts(
  stay: { requestedBedConfig?: string | null },
  r: { notes: string | null; sourceText?: string | null; arrivalWindowFrom?: string | null; arrivalWindowTo?: string | null } | null,
): string[] {
  const out: string[] = [];
  if (stay.requestedBedConfig) out.push(stay.requestedBedConfig);
  const arrival = fmtArrivalWindow(r?.arrivalWindowFrom, r?.arrivalWindowTo);
  if (arrival !== null) out.push(arrival);
  const note = bookingComment(r);
  if (note !== null) out.push(note.length > 60 ? `${note.slice(0, 59)}…` : note);
  return out;
}

/** « Lit demandé » in words, and whether the étage has work to do. `current` is
 *  how the ASSIGNED room stands (null = not known, undefined = no room yet). */
export function bedRequestFr(
  wanted: string | null | undefined,
  labelOf: (code: string) => string,
  current?: string | null,
): { text: string; warn: boolean } {
  if (!wanted) return { text: 'Aucune demande', warn: false };
  const asked = labelOf(wanted);
  if (current === undefined) return { text: asked, warn: false };
  if (current === null) return { text: `${asked} — état de la chambre inconnu`, warn: false };
  return current === wanted
    ? { text: `${asked} — chambre prête`, warn: false }
    : { text: `${asked} — chambre actuellement en ${labelOf(current)}`, warn: true };
}

// ── « Tetris » — the proposal, in the desk's words ───────────────────────────

/** Why a proposed room is where it is in the list, one short phrase per fact.
 *  The ranking itself is `proposeRoomAssignment` (pms-core); this only says it.
 *  `warn` = the proposal carries a cost the desk should weigh: another
 *  category, a stranded night, or a bed the étage must change. */
export function assignmentReasonsFr(
  p: {
    sameCategory: boolean;
    before: { stayId: string; gapNights: number } | null;
    after: { stayId: string; gapNights: number } | null;
    strandedBefore: number;
    strandedAfter: number;
    bedReady: boolean | null;
    nearSiblings: boolean | null;
  },
  ctx: {
    categoryCode: string | null;
    nameOf: (stayId: string) => string;
    wanted: string | null;
    current: string | null;
  },
): { reasons: string[]; warn: boolean } {
  const reasons: string[] = [];
  let warn = false;
  if (!p.sameCategory) {
    reasons.push(`autre catégorie (${ctx.categoryCode ?? '?'})`);
    warn = true;
  }
  const nights = (n: number) => `${n} nuit${n > 1 ? 's' : ''}`;
  if (p.before?.gapNights === 0) reasons.push(`colle au départ de ${ctx.nameOf(p.before.stayId)}`);
  if (p.after?.gapNights === 0) reasons.push(`colle à l'arrivée de ${ctx.nameOf(p.after.stayId)}`);
  // Which side strands the nights is worth saying — it is the one to fix.
  const stranded = (n: number, side: string) => `laisse ${nights(n)} libre${n > 1 ? 's' : ''} ${side}`;
  if (p.strandedBefore > 0) reasons.push(stranded(p.strandedBefore, 'avant'));
  if (p.strandedAfter > 0) reasons.push(stranded(p.strandedAfter, 'après'));
  if (p.strandedBefore > 0 || p.strandedAfter > 0) warn = true;
  if (ctx.wanted !== null) {
    if (p.bedReady === true) reasons.push(`lit déjà en ${ctx.wanted}`);
    else if (p.bedReady === false) {
      reasons.push(`lit à passer en ${ctx.wanted} (actuellement ${ctx.current ?? '?'})`);
      warn = true;
    } else reasons.push('état du lit inconnu');
  }
  if (p.nearSiblings === true) reasons.push('même étage que la réservation');
  if (reasons.length === 0) reasons.push('libre');
  return { reasons, warn };
}

/** « Aucune DSTD libre : 9 occupées · 1 trop petite » — what an empty or thin
 *  list means. Null when nothing of the booked category was turned away. */
export function assignmentRejectionsFr(
  rejected: { occupied: number; capacity: number; bed: number },
  categoryCode: string | null,
  wanted: string | null,
): string | null {
  const parts: string[] = [];
  const s = (n: number) => (n > 1 ? 's' : '');
  if (rejected.occupied > 0) parts.push(`${rejected.occupied} occupée${s(rejected.occupied)}`);
  if (rejected.capacity > 0) parts.push(`${rejected.capacity} trop petite${s(rejected.capacity)}`);
  if (rejected.bed > 0) parts.push(`${rejected.bed} sans ${wanted ?? 'le lit demandé'}`);
  return parts.length === 0 ? null : `${categoryCode ?? 'Catégorie'} écartées : ${parts.join(' · ')}`;
}
